import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from '@mui/material';
import { TeamRole } from 'api/management/team.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';

interface CreateTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; role: TeamRole; warehouseId: string }) => void;
  loading?: boolean;
  preselectedWarehouseId?: string | null;
  preselectedWarehouseName?: string | null;
}

const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  preselectedWarehouseId,
  preselectedWarehouseName,
}) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState<TeamRole | ''>('');
  const [warehouseId, setWarehouseId] = useState(preselectedWarehouseId || '');
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  // Initialize warehouse if preselected
  useEffect(() => {
    if (preselectedWarehouseId) {
      setWarehouseId(preselectedWarehouseId);
    }
  }, [preselectedWarehouseId]);

  const fetchWarehouses = () => {
    setWarehousesLoading(true);
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        const warehouseList = response.objects.map((w) => ({ id: w.id, name: w.name }));
        setWarehouses(warehouseList);
        setWarehousesLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        setWarehousesLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open && !preselectedWarehouseId) {
      fetchWarehouses();
    }
  }, [open, preselectedWarehouseId]);

  const handleSubmit = () => {
    if (name && role && warehouseId) {
      onSubmit({ name, role: role as TeamRole, warehouseId });
    }
  };

  const handleClose = () => {
    setName('');
    setRole('');
    setWarehouseId('');
    onClose();
  };

  const isFormValid = name.trim() !== '' && role !== '' && warehouseId !== '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Criar Nova Equipa</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Warehouse field - first and locked if preselected */}
          {preselectedWarehouseId && preselectedWarehouseName ? (
            <TextField fullWidth label="Armazém" value={preselectedWarehouseName} disabled sx={{ mb: 2 }} />
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Armazém</InputLabel>
              <Select
                value={warehouseId}
                label="Armazém"
                onChange={(e) => setWarehouseId(e.target.value)}
                disabled={loading || warehousesLoading}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Nome da Equipa"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
            disabled={loading}
          />

          <FormControl fullWidth>
            <InputLabel>Função</InputLabel>
            <Select value={role} label="Função" onChange={(e) => setRole(e.target.value as TeamRole)} disabled={loading}>
              <MenuItem value={TeamRole.INVENTORY}>Inventário</MenuItem>
              <MenuItem value={TeamRole.RECEPTION}>Receção</MenuItem>
              <MenuItem value={TeamRole.PICKING}>Expedição</MenuItem>
              <MenuItem value={TeamRole.PRODUCTION}>Produção</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'A criar...' : 'Criar Equipa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTeamDialog;
